import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { resolveImg, inr } from "../lib/api";
import { useCart } from "../context/CartContext";

const CartPage = () => {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div className="max-w-3xl mx-auto px-4 py-24 text-center">
        <ShoppingBag size={56} className="mx-auto text-[#C5A059]" />
        <h1 className="font-serif font-bold text-3xl text-[#1C2B20] mt-6">Your cart is empty</h1>
        <p className="text-[#5A6B5D] mt-2">Looks like you haven't added any wellness yet.</p>
        <Link
          to="/shop"
          className="mt-8 inline-flex items-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] text-white font-medium rounded-full px-8 py-3 transition-colors"
          data-testid="cart-empty-shop-btn"
        >
          Start Shopping <ArrowRight size={18} />
        </Link>
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <h1 className="font-serif font-bold text-3xl sm:text-4xl text-[#1C2B20] mb-8">Shopping Cart</h1>
      <div className="grid lg:grid-cols-[1fr_360px] gap-8">
        <div className="space-y-4">
          {items.map((item) => (
            <div
              key={item.product_id}
              className="flex gap-4 bg-white rounded-2xl border border-[#E2DAD0] p-4"
              data-testid={`cart-item-${item.product_id}`}
            >
              <img
                src={resolveImg(item.image_url)}
                alt={item.name}
                className="h-24 w-24 rounded-xl object-cover bg-[#FAF7F2] shrink-0"
              />
              <div className="flex-1 min-w-0">
                <Link to={`/product/${item.product_id}`} className="font-semibold text-[#1C2B20] hover:text-[#1F4D2E] line-clamp-2">
                  {item.name}
                </Link>
                <p className="text-[#1F4D2E] font-bold mt-1">{inr(item.price)}</p>
                <div className="mt-3 flex items-center justify-between">
                  <div className="flex items-center border border-[#E2DAD0] rounded-full">
                    <button
                      onClick={() => updateQty(item.product_id, item.quantity - 1)}
                      className="p-2 text-[#1F4D2E]"
                      data-testid={`cart-dec-${item.product_id}`}
                    >
                      <Minus size={14} />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                    <button
                      onClick={() => updateQty(item.product_id, item.quantity + 1)}
                      className="p-2 text-[#1F4D2E]"
                      data-testid={`cart-inc-${item.product_id}`}
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(item.product_id)}
                    className="text-[#C86D51] hover:text-red-700 flex items-center gap-1 text-sm"
                    data-testid={`cart-remove-${item.product_id}`}
                  >
                    <Trash2 size={16} /> Remove
                  </button>
                </div>
              </div>
              <div className="font-bold text-[#1C2B20] shrink-0">{inr(item.price * item.quantity)}</div>
            </div>
          ))}
        </div>

        <div className="lg:sticky lg:top-24 h-fit bg-white rounded-2xl border border-[#E2DAD0] p-6">
          <h2 className="font-serif text-xl font-bold text-[#1C2B20] mb-5">Order Summary</h2>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between text-[#5A6B5D]">
              <span>Subtotal</span>
              <span className="font-semibold text-[#1C2B20]" data-testid="cart-subtotal">{inr(subtotal)}</span>
            </div>
            <div className="flex justify-between text-[#5A6B5D]">
              <span>Shipping</span>
              <span className="text-[#1F4D2E] font-medium">Free</span>
            </div>
            <div className="border-t border-[#E2DAD0] pt-3 flex justify-between text-base font-bold text-[#1C2B20]">
              <span>Total</span>
              <span>{inr(subtotal)}</span>
            </div>
          </div>
          <div className="mt-4 text-xs text-[#8A9A8D] bg-[#F5F0E6] rounded-lg p-3">
            Pay a ₹200 advance online to confirm. Balance collected as Cash on Delivery.
          </div>
          <button
            onClick={() => navigate("/checkout")}
            data-testid="cart-checkout-btn"
            className="mt-5 w-full flex items-center justify-center gap-2 bg-[#1F4D2E] hover:bg-[#163A22] text-white font-medium rounded-full py-3.5 transition-colors"
          >
            Proceed to Checkout <ArrowRight size={18} />
          </button>
          <Link to="/shop" className="mt-3 block text-center text-sm text-[#1F4D2E] font-medium hover:underline">
            Continue Shopping
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CartPage;
